import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { ApiConnectionsCard } from './connections/ApiConnectionsCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="text-sm font-medium text-foreground">{label}</span>
    {children}
    {hint && <span className="block text-xs text-muted-foreground mt-1">{hint}</span>}
  </label>
);

export const LocationAIDialog = ({ businessId, location, onClose, onSaved }) => {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const url = `${API}/business/${businessId}/locations/${location.id}/ai`;

  useEffect(() => {
    axios.get(url).then((r) => setF(r.data)).catch(() => { toast.error('Could not load this location'); onClose(); });
  }, [url]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await axios.put(url, { greeting: f.greeting, hours: f.hours, transfer_phone_number: f.transfer_phone_number, instructions: f.instructions });
      toast.success(r.data.message); onSaved?.();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not save');
    }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="location-ai-dialog">
        <DialogHeader><DialogTitle>Pro for {location.name}</DialogTitle></DialogHeader>
        {!f ? <Loader2 className="w-5 h-5 animate-spin" /> : (
          <div className="space-y-6">
            <form onSubmit={save} className="space-y-4">
              <p className="text-sm text-muted-foreground">This location has its own Pro{f.phone_number ? ` on ${f.phone_number}` : ''}. These settings only apply here.</p>
              <Field label="Greeting" hint="The first thing callers hear.">
                <Textarea rows={2} value={f.greeting} onChange={set('greeting')} required minLength={5} placeholder={`Thanks for calling ${location.name}, how can I help?`} data-testid="location-ai-greeting-input" />
              </Field>
              <Field label="Opening hours"><Input value={f.hours} onChange={set('hours')} placeholder="Mon-Fri 9am-6pm, Sat 10am-2pm" data-testid="location-ai-hours-input" /></Field>
              <Field label="Transfer calls to" hint="Who picks up when a caller asks for a person. Leave blank to not transfer.">
                <Input value={f.transfer_phone_number} onChange={set('transfer_phone_number')} placeholder="(555) 123-4567" data-testid="location-ai-transfer-input" />
              </Field>
              <Field label="Anything else your Pro should know about this location">
                <Textarea rows={3} value={f.instructions} onChange={set('instructions')} placeholder="Parking is behind the building. We only do walk-ins here." data-testid="location-ai-instructions-input" />
              </Field>
              <Button type="submit" disabled={busy} data-testid="location-ai-save-btn">{busy ? 'Saving...' : 'Save location Pro'}</Button>
            </form>
            <div>
              <h3 className="font-semibold text-foreground mb-2">Connected system for this location</h3>
              <ApiConnectionsCard locationId={location.id} businessId={businessId} embedded />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
